import type { Pathway } from "../types";

import { links } from "../../linksRegistry";

export const impetigoPathway: Pathway = {
  id: "impetigo",
  name: "Impetigo (non-bullous)",
  population:
    "Adults and children aged 1 year and over (exclude: bullous, recurrent, pregnant under 16)",
  pgdRefs: [
    "protocol-3a-h2o2-impetigo",
    "pgd-3b-fusidic-impetigo",
    "pgd-3c-fluclox-impetigo",
    "pgd-3d-clari-impetigo",
    "pgd-3e-eryth-impetigo",
  ],
  links,
  clinicalNotes: [
    "Confirm likely presentation of impetigo through visual examination in a consultation room.",
    "Non-bullous impetigo: thin-walled vesicle on erythematous base → ruptures → golden yellow/brown crusts.",
    "Bullous impetigo is excluded from this pathway — refer.",
    "Advise all patients on good hygiene to reduce spread.",
  ],
  clinicalNoteLinkRefs: ["nice-cks-impetigo", "nhs-england-pharmacy-first-clinical-pathways", "pgd-impetigo-hydrogen-peroxide", "pgd-impetigo-fusidic-acid", "pgd-impetigo-flucloxacillin", "pgd-impetigo-clarithromycin", "pgd-impetigo-erythromycin"],
  demographicConfiguration: {
    bannerFields: [
      { field: "dateOfBirth", label: "Date of birth", type: "date" },
      { field: "ageYears", label: "Age (years)", type: "number" },
      {
        field: "knownAllergies",
        label: "Known allergies",
        type: "string",
        helpText: "Penicillin/macrolide allergies are asked again in the flow where relevant",
      },
    ],
  },
  exclusionPrefillMap: {},
  entryNode: "q1_emergency",
  nodes: [
    {
      id: "q1_emergency",
      type: "checklist",
      text: "Any emergency presentations?",
      showSepsisWarning: true,
      items: [
        { id: "severe-complication", text: "Severe complications suspected (e.g. deeper soft tissue infection, cellulitis, Staphylococcal scalded skin syndrome)" },
        { id: "sepsis", text: "Signs of sepsis or severe/life-threatening infection" },
        { id: "immuno-widespread", text: "Patient is immunosuppressed AND infection appears widespread" },
      ],
      includeNoneOption: true,
      anySelected: "GATEWAY_EMERGENCY",
      noneSelected: "q2_clinical_features",
    },
    {
      id: "GATEWAY_EMERGENCY",
      type: "gateway_alarm",
      heading: "EMERGENCY — A&E referral",
      body: "Consider calculating NEWS2 Score. Signpost to A&E or call 999.",
      action: "close",
      triggersPayment: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score (optional)",
      leafletRefs: ["leaflet-impetigo-en"],
    },
    {
      id: "q2_clinical_features",
      type: "yes_no",
      text: "Does the patient follow typical progression of non-bullous impetigo clinical features?",
      hint: "Thin-walled vesicle on erythematous base → ruptures → golden yellow/brown crusts. Most common on face, limbs, flexures. Satellite lesions. Usually asymptomatic or mildly itchy.",
      required: true,
      storesTo: "confirmedNonBullous",

      imagePopover: {
        label: "View example images",
        items: [
          {
            imageUrl:
              "https://cdn.builder.io/api/v1/image/assets%2F0407d02f319145f582bec36d8e4031e2%2Fd9e9065a23724a86b8c41bb98ff0104e",
            alt: "Impetigo example beneath the mouth",
            caption:
              "After the blisters burst, you often have golden-brown crusty patches on your skin.",
            heightPx: 240,
          },
          {
            imageUrl:
              "https://cdn.builder.io/api/v1/image/assets%2F0407d02f319145f582bec36d8e4031e2%2Faba0ce58440c43d0bf31f0dd353e3ef2",
            alt: "Impetigo example on a child's face",
            caption:
              "The sores and blisters of impetigo commonly affect the face (around the nose and mouth) and hands.",
            heightPx: 240,
          },
          {
            imageUrl:
              "https://cdn.builder.io/api/v1/image/assets%2F0407d02f319145f582bec36d8e4031e2%2F3fac89f5b8b04a7daf14797656015aab",
            alt: "Bullous impetigo example",
            caption:
              "Sometimes yellow liquid oozes from the blisters and hardens to leave a scaly border around a scab. This is called bullous impetigo.",
            heightPx: 240,
          },
        ],
      },
      yes: "GATEWAY_IMPETIGO_LIKELY",
      no: { outcomeId: "OUT_ALTERNATIVE_DIAGNOSIS" },
    },
    {
      id: "GATEWAY_IMPETIGO_LIKELY",
      type: "gateway_progression",
      heading: "Impetigo more likely",
      body: "Clinical features consistent with non-bullous impetigo.",
      triggersPayment: true,
      next: "q3_recurrent",
    },
    {
      id: "q3_recurrent",
      type: "yes_no",
      text: "Is this recurrent impetigo? (2 or more episodes in the same year)",
      required: true,
      yes: { outcomeId: "OUT_REFER_RECURRENT" },
      no: "q4_underlying_skin_condition",
    },
    {
      id: "q4_underlying_skin_condition",
      type: "yes_no",
      text: "Does patient have a currently active underlying skin condition (e.g. eczema, dermatitis, scabies)?",
      required: true,
      yes: { outcomeId: "OUT_REFER_UNDERLYING_SKIN" },
      no: "q5_lesion_count",
    },
    {
      id: "q5_lesion_count",
      type: "single_select",
      text: "How many lesions/clusters are present?",
      required: true,
      storesTo: "isWidespread",
      options: [
        { id: "localised", label: "≤3 lesions/clusters (localised)" },
        { id: "widespread", label: "≥4 lesions/clusters (widespread)" },
      ],
      routing: {
        localised: "q_localised_around_eyes",
        widespread: "q_widespread_allergy",
      },
    },
    {
      id: "q_localised_around_eyes",
      type: "yes_no",
      text: "Is the impetigo located around the eyes?",
      required: true,
      storesTo: "impetigoAroundEyes",
      yes: "localised_fusidic_checks",
      no: "q_localised_h2o2_tried",
    },
    {
      id: "q_localised_h2o2_tried",
      type: "yes_no",
      text: "Has hydrogen peroxide cream been tried before for this impetigo?",
      hint: "Ask if first-line treatment has been used previously.",
      required: true,
      storesTo: "h2o2Tried",
      yes: "localised_fusidic_checks",
      no: "localised_h2o2_checks",
    },
    {
      id: "localised_h2o2_checks",
      type: "pgd_exclusion_check",
      pgdIdOverride: "protocol-3a-h2o2-impetigo",
      anyTriggered: "q_h2o2_unsuitable",
      allCleared: { outcomeId: "OUT_SUPPLY_H2O2" },
    },
    {
      id: "q_h2o2_unsuitable",
      type: "yes_no",
      text: "Is fusidic acid cream appropriate as second-line treatment?",
      hint: "Use if hydrogen peroxide is unsuitable (e.g. around eyes) or ineffective and impetigo remains localised.",
      required: true,
      yes: "localised_fusidic_checks",
      no: { outcomeId: "OUT_REFER_TOPICAL_UNSUITABLE" },
    },
    {
      id: "localised_fusidic_checks",
      type: "pgd_exclusion_check",
      pgdIdOverride: "pgd-3b-fusidic-impetigo",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY_FUSIDIC" },
      allCleared: { outcomeId: "OUT_SUPPLY_FUSIDIC" },
    },
    {
      id: "q_widespread_allergy",
      type: "yes_no",
      text: "Does the patient have a known penicillin allergy? (Including allergy to flucloxacillin or severe hypersensitivity to any beta-lactam)?",
      required: true,
      storesTo: "hasPenicillinAllergy",
      yes: "q_widespread_pregnant",
      no: "q_widespread_pregnant",
    },
    {
      id: "q_widespread_pregnant",
      type: "yes_no",
      text: "Is the patient currently pregnant or suspected pregnant?",
      required: true,
      storesTo: "isPregnant",
      autoAnswer: { when: { patientIsFemale: false }, value: false },
      yes: "q_widespread_pregnant_age_gate",
      no: "widespread_drug_select",
    },
    {
      id: "q_widespread_pregnant_age_gate",
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 16 }, then: { outcomeId: "OUT_CANNOT_PREGNANT_UNDER16" } },
      ],
      default: "widespread_drug_select",
    },
    {
      id: "widespread_drug_select",
      type: "drug_selection",
      variant: "auto",
      rules: [
        { when: { hasPenicillinAllergy: false, isPregnant: false }, pgdId: "pgd-3c-fluclox-impetigo" },
        { when: { hasPenicillinAllergy: false, isPregnant: true }, pgdId: "pgd-3c-fluclox-impetigo" },
        { when: { hasPenicillinAllergy: true, isPregnant: false }, pgdId: "pgd-3d-clari-impetigo" },
        { when: { hasPenicillinAllergy: true, isPregnant: true }, pgdId: "pgd-3e-eryth-impetigo" },
      ],
      next: "weight_capture",
    },
    {
      id: "weight_capture",
      type: "weight_capture",
      text: "Confirm patient weight (kg) for paediatric clarithromycin dosing",
      hint: "Only required for children aged 1–11 receiving clarithromycin under PGD 3d.",
      minKg: 0.5,
      maxKg: 250,
      next: "widespread_pgd_exclusions",
    },
    {
      id: "widespread_pgd_exclusions",
      type: "pgd_exclusion_check",
      anyTriggered: { outcomeId: "OUT_CANNOT_SUPPLY_ORAL" },
      allCleared: { outcomeId: "OUT_SUPPLY_ORAL" },
    },
  ],
  outcomes: {
    OUT_SUPPLY_H2O2: { id: "OUT_SUPPLY_H2O2", type: "outcome", outcomeType: "supply", drugRef: "hydrogen_peroxide_cream", title: "Supply Hydrogen Peroxide 1% Cream", reason: "Localised non-bullous impetigo — first-line protocol supply.", safetyNetting: ["Return to pharmacy if symptoms worsen at any time", "Return if no improvement after completing the 5-day course", "If impetigo becomes widespread, oral antibiotics may be needed"], leafletRefs: ["leaflet-impetigo-en"], linkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"] },
    OUT_SUPPLY_FUSIDIC: { id: "OUT_SUPPLY_FUSIDIC", type: "outcome", outcomeType: "supply", drugRef: "fusidic_acid_cream", title: "Supply Fusidic Acid 2% Cream", reason: "Localised non-bullous impetigo — second-line topical supply.", safetyNetting: ["Return to pharmacy if symptoms worsen at any time", "Return if no improvement after completing the 5-day course", "If impetigo becomes widespread, oral antibiotics may be needed"], leafletRefs: ["leaflet-impetigo-en"], linkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"] },
    OUT_SUPPLY_ORAL: { id: "OUT_SUPPLY_ORAL", type: "outcome", outcomeType: "supply", title: "Supply oral antibiotic under PGD", reason: "Widespread non-bullous impetigo and PGD checks cleared.", safetyNetting: ["If symptoms worsen rapidly or significantly at any time, seek urgent medical advice", "If symptoms do not improve after completion of 5-day treatment course, refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"], linkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"] },
    OUT_ALTERNATIVE_DIAGNOSIS: { id: "OUT_ALTERNATIVE_DIAGNOSIS", type: "outcome", outcomeType: "refer_routine", title: "Consider alternative diagnosis", reason: "Clinical features not consistent with non-bullous impetigo.", safetyNetting: ["Consider alternative diagnosis and proceed appropriately"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_BULLOUS: { id: "OUT_REFER_BULLOUS", type: "outcome", outcomeType: "refer_routine", title: "Refer — bullous impetigo", reason: "Bullous impetigo is excluded from this pathway.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_RECURRENT: { id: "OUT_REFER_RECURRENT", type: "outcome", outcomeType: "refer_routine", title: "Refer — recurrent impetigo", reason: "2 or more episodes in the same year.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_UNWELL: { id: "OUT_REFER_UNWELL", type: "outcome", outcomeType: "refer_routine", title: "Refer — systemically unwell", reason: "Patient is systemically unwell but not showing signs of sepsis.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_UNDERLYING_SKIN: { id: "OUT_REFER_UNDERLYING_SKIN", type: "outcome", outcomeType: "refer_routine", title: "Refer — underlying skin condition", reason: "Patient has a currently active underlying skin condition (e.g. eczema, dermatitis, scabies).", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_IMMUNO: { id: "OUT_REFER_IMMUNO", type: "outcome", outcomeType: "refer_urgent", title: "Refer — severely immunosuppressed", reason: "Severely immunosuppressed individuals are excluded from this pathway.", safetyNetting: ["Refer to prescriber or urgent service as clinically appropriate"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_TOPICAL_UNSUITABLE: { id: "OUT_REFER_TOPICAL_UNSUITABLE", type: "outcome", outcomeType: "refer_routine", title: "Refer — topical treatments not suitable", reason: "Both hydrogen peroxide and fusidic acid are not suitable. Impetigo is localised so oral antibiotics are not indicated.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_CANNOT_SUPPLY_FUSIDIC: { id: "OUT_CANNOT_SUPPLY_FUSIDIC", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply fusidic acid — PGD exclusion met", reason: "One or more fusidic acid PGD exclusions were triggered.", safetyNetting: ["Refer to prescriber if treatment appropriate but falls outside this PGD"], leafletRefs: ["leaflet-impetigo-en"], linkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"] },
    OUT_CANNOT_SUPPLY_ORAL: { id: "OUT_CANNOT_SUPPLY_ORAL", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply oral antibiotic — PGD exclusion met", reason: "One or more oral antibiotic PGD exclusions were triggered.", safetyNetting: ["Refer to prescriber if antibiotic appropriate but falls outside this PGD"], leafletRefs: ["leaflet-impetigo-en"], linkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"] },
    OUT_CANNOT_UNDER1: { id: "OUT_CANNOT_UNDER1", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply — under 1 year of age", reason: "Refer to GP or other provider.", safetyNetting: ["Refer to GP or other provider"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_CANNOT_PREGNANT_UNDER16: { id: "OUT_CANNOT_PREGNANT_UNDER16", type: "outcome", outcomeType: "cannot_supply", title: "Cannot supply — pregnant and under 16", reason: "Refer to GP or other provider.", safetyNetting: ["Refer same day", "Return urgently if symptoms worsen"], leafletRefs: ["leaflet-impetigo-en"] },
    OUT_REFER_UNCLEAR: { id: "OUT_REFER_UNCLEAR", type: "outcome", outcomeType: "refer_routine", title: "Refer — extent of impetigo unclear", reason: "Unable to determine whether impetigo is localised or widespread.", safetyNetting: ["Refer to GP or other provider for assessment"], leafletRefs: ["leaflet-impetigo-en"] },
  },
};
